var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { FreelancerHttp, HttpEventType } from '@freelancer/freelancer-http';
import { filter, last, map, publishReplay, refCount, startWith, take, } from 'rxjs/operators';
import * as uuidv4 from 'uuid/v4';
import { configs, } from './file-storage-backend.config';
import * as i0 from "@angular/core";
import * as i1 from "../freelancer-http/freelancer-http.service";
var UploadTask = /** @class */ (function () {
    function UploadTask(upload$) {
        this.upload$ = upload$;
        // starts the upload. call cancel() to cancel it.
        this.upload$.subscribe();
    }
    /*
     * Emits the UploadTaskEvent as the file upload progresses.
     * Completes when the upload has completed (success or error),
     * i.e. you can use `last` to only get the upload result:
     * this.snapshot   = this.task.changes().pipe(
     *   last(() =>  {
     *     this.downloadURL = fileRef.getDownloadURL() )
     */
    UploadTask.prototype.changes = function () {
        return this.upload$;
    };
    /*
     * Emits the upload progress as a number between 0 and 100, that can easily
     * be piped into a <progress> element's value or similar Bits component
     */
    UploadTask.prototype.percentageChanges = function () {
        return this.upload$.pipe(map(function (s) { return (s.bytesTransferred / s.totalBytes) * 100; }));
    };
    /*
     * Emits the download URL of the file once the upload is completed.
     * Use that to pipe it to image source for instance
     * (<img [src]="profileUrl | async" />), but not for user-triggered
     * actions as there's no error handling (it'll just never emit).
     */
    UploadTask.prototype.getDownloadURL = function () {
        return this.upload$.pipe(last(), map(function (s) { return s.downloadURL; }), filter(isDefined));
    };
    /*
     * Cancel an upload in progress.
     */
    UploadTask.prototype.cancel = function () {
        this.uploadSubscription.unsubscribe();
    };
    return UploadTask;
}());
export { UploadTask };
var FileStorage = /** @class */ (function () {
    function FileStorage(freelancerHttp) {
        this.freelancerHttp = freelancerHttp;
    }
    /*
     * Upload a new file to the storage bucket. The fileId gets randomly
     * generated and will be returned with the first `UploadTaskEvent`.
     *
     * DEPRECATED: for storage backends relying on awful backend file ids, the
     * randomly generated fileId won't be used and the backend id will be emitted
     * with the last `UploadTaskEvent`.
     */
    FileStorage.prototype.upload = function (bucket, file, metadata) {
        var id = uuidv4();
        return this._upload(bucket, id, file, metadata);
    };
    /*
     * Replace an existing file, specified by its file id, with a new one.
     */
    FileStorage.prototype.replace = function (bucket, id, file, metadata) {
        return this._upload(bucket, id, file, metadata);
    };
    /*
     * Not implemented, but feel free to
     */
    FileStorage.prototype.delete = function (bucket, id) {
        throw new Error('Not implemented');
    };
    /*
     * Returns an standard error object when the download fails, e.g. due to a
     * permission error. The FileStorage service has not and MUST not have any
     * concept of permisions, i.e. the DriveFile model is just plain wrong.
     */
    FileStorage.prototype.getDownloadURL = function (bucket, id) {
        var config = configs[bucket]({ bucket: bucket, id: id });
        if (!config.downloadURLEndpoint) {
            throw new Error('Storage backend does not support getting the file download URL');
        }
        return this.freelancerHttp
            .get(config.downloadURLEndpoint)
            .pipe(take(1))
            .toPromise()
            .then(function (response) {
            if (response.status === 'success') {
                if (!config.downloadURL) {
                    throw new Error('No downloadURL parsing method has been defined for that storage');
                }
                return {
                    status: 'success',
                    downloadURL: config.downloadURL(response.result),
                };
            }
            return {
                status: 'error',
                errorCode: response.errorCode,
                requestId: response.requestId,
            };
        });
    };
    /*
     * Not implemented, but feel free to
     */
    FileStorage.prototype.getMetadata = function (bucket, id) {
        throw new Error('Not implemented');
    };
    /*
     * Not implemented, but feel free to
     */
    FileStorage.prototype.updateMetadata = function (bucket, id, metadata) {
        throw new Error('Not implemented');
    };
    FileStorage.prototype._upload = function (bucket, id, file, metadata) {
        var uploadMetadata = __assign({}, (metadata || {}), { bucket: bucket,
            id: id, name: file.name, size: file.size, timeCreated: Date.now(), updated: Date.now() });
        var config = configs[bucket]({ bucket: bucket, id: id });
        var formData = new FormData();
        formData.append(config.fileParam, file);
        formData.append('metadata', JSON.stringify(uploadMetadata));
        if (config.DEPRECATED_extraParams) {
            Object.entries(config.DEPRECATED_extraParams(uploadMetadata)).forEach(function (_a) {
                var key = _a[0], value = _a[1];
                formData.append(key, value);
            });
        }
        var startEvent = {
            state: 'running',
            ref: { bucket: bucket, id: id },
            metadata: uploadMetadata,
            bytesTransferred: 0,
            totalBytes: file.size,
        };
        var upload$ = this.freelancerHttp
            .post(config.DEPRECATED_funkyChangingEndpoint
            ? config.DEPRECATED_funkyChangingEndpoint(uploadMetadata)
            : config.endpoint, formData, {
            reportProgress: true,
            isGaf: config.isGaf || false,
        })
            .pipe(map(function (event) {
            if (event.type === HttpEventType.UploadProgress) {
                return __assign({}, startEvent, { bytesTransferred: event.loaded });
            }
            if (event.type === HttpEventType.Response && event.body) {
                if (event.body.status === 'success') {
                    return __assign({}, startEvent, { state: 'success', bytesTransferred: startEvent.totalBytes, ref: __assign({}, startEvent.ref, { DEPREACTED_DO_NOT_USE_backendId: config.DEPREACTED_DO_NOT_USE_backendId
                                ? config.DEPREACTED_DO_NOT_USE_backendId(event.body
                                    .result)
                                : undefined }) });
                }
                return __assign({}, startEvent, { state: 'error', bytesTransferred: startEvent.totalBytes, error: {
                        errorCode: event.body.errorCode,
                        requestId: event.body.requestId,
                    } });
            }
        }), filter(isDefined), startWith(startEvent), publishReplay(1), refCount());
        return new UploadTask(upload$);
    };
    FileStorage.ngInjectableDef = i0.defineInjectable({ factory: function FileStorage_Factory() { return new FileStorage(i0.inject(i1.FreelancerHttp)); }, token: FileStorage, providedIn: "root" });
    return FileStorage;
}());
export { FileStorage };
export function isDefined(x) {
    return x !== undefined && x !== null;
}
